(() => {
  const cfg = (() => {
    if (window.GWP_CONFIG && typeof window.GWP_CONFIG === "object") return window.GWP_CONFIG;
    const el = document.getElementById("GwpConfig");
    if (!el) return {};
    const raw =
      (el.tagName === "TEMPLATE" ? el.content?.textContent || el.innerHTML : el.textContent) || "";
    try {
      return JSON.parse(raw.trim() || "{}");
    } catch (_) {
      return {};
    }
  })();
  if (!cfg.enabled) return;

  const variantId = Number.parseInt(String(cfg.variantId || "").trim(), 10);
  if (!Number.isFinite(variantId) || variantId <= 0) return;

  const threshold = Math.max(1, Number.parseInt(cfg.threshold, 10) || 1);

  const LINE_ITEM_PROP = cfg.lineItemProperty || "_gwp_gift";
  const PREORDER_PROP = "Pre-order";
  const SOURCE = "gwp-auto-add";
  const CART_UPDATE_EVENT =
    (typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS?.cartUpdate) ||
    window.PUB_SUB_EVENTS?.cartUpdate;

  let inFlight = false;
  let queued = false;
  let lastSignature = null;
  let lastHadGift = false;
  let systemRemovingGift = false;
  let systemAddingGift = false;

  const optOutStorageKey = `gwp_opt_out_v1:${variantId}`;

  const isOptedOut = () => {
    try {
      return window.sessionStorage?.getItem(optOutStorageKey) === "1";
    } catch (_) {
      return false;
    }
  };

  const setOptedOut = () => {
    try {
      window.sessionStorage?.setItem(optOutStorageKey, "1");
    } catch (_) {}
  };

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const cartJsUrl = `${root}cart.js`;
  const addUrl = `${root}cart/add.js`;
  const changeUrl = `${root}cart/change.js`;
  const SECTIONS_TO_RENDER = ["cart-icon-bubble", "cart-bundle-progress", "cart-drawer"];
  const SECTIONS_PARAM = SECTIONS_TO_RENDER.join(",");

  const postJson = async (url, payload) => {
    const config =
      typeof fetchConfig === "function"
        ? { ...fetchConfig("json"), body: JSON.stringify(payload) }
        : {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(payload),
          };

    const res = await fetch(url, config);
    const data = await res.json().catch(() => null);
    if (!res.ok || data?.status) {
      const message = data?.description || data?.message || `GWP request failed: ${res.status}`;
      throw new Error(message);
    }
    return data;
  };

  const fetchCart = async () => {
    const res = await fetch(cartJsUrl, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
    return res.json();
  };

  const parseCountAttr = (html) => {
    if (!html) return null;
    const match =
      String(html).match(/data-gwp-qualifying-count=["'](\d+)["']/) ||
      String(html).match(/data-bundle-tracker-count=["'](\d+)["']/);
    if (!match) return null;
    const count = Number.parseInt(match[1], 10);
    return Number.isFinite(count) && count >= 0 ? count : null;
  };

  const readDomCount = () => {
    const gwpEl = document.querySelector("[data-gwp-qualifying-count]");
    if (gwpEl) {
      const parsed = parseCountAttr(gwpEl.outerHTML);
      if (parsed != null) return parsed;
    }
    const bundleEl = document.querySelector("[data-bundle-tracker-count]");
    if (bundleEl) {
      const parsed = parseCountAttr(bundleEl.outerHTML);
      if (parsed != null) return parsed;
    }
    return null;
  };

  const fetchQualifyingCount = async () => {
    try {
      const url = `${root}?sections=cart-bundle-progress&sections_url=${encodeURIComponent(window.location.pathname)}`;
      const res = await fetch(url, {
        method: "GET",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        const data = await res.json().catch(() => null);
        const parsed = parseCountAttr(data?.["cart-bundle-progress"]);
        if (parsed != null) return parsed;
      }
    } catch (_) {}
    return readDomCount() ?? 0;
  };

  const isGiftItem = (item) => {
    const props = item && item.properties;
    if (!props) return false;
    const v = props[LINE_ITEM_PROP];
    return v === true || v === "true" || v === 1 || v === "1";
  };

  const giftLinePreorderDate = (item) => {
    const v = item && item.properties && item.properties[PREORDER_PROP];
    return String(v || "").replace(/\s+/g, " ").trim();
  };

  const parsePreorderDateAttr = (html) => {
    if (!html) return "";
    const match = String(html).match(/data-gwp-preorder-date=["']([^"']+)["']/);
    return match ? String(match[1]).replace(/\s+/g, " ").trim() : "";
  };

  const dateFromResponse = (response) => {
    const sections = response?.sections || {};
    return (
      parsePreorderDateAttr(sections["cart-drawer"]) ||
      parsePreorderDateAttr(sections["cart-bundle-progress"]) ||
      ""
    );
  };

  const configuredPreorderDate = () => {
    const raw = cfg.isUsMarket ? cfg.usPreorderDate : cfg.preorderDate;
    return String(raw || "").replace(/\s+/g, " ").trim();
  };

  const readDomPreorderDate = () => {
    const el = document.querySelector("[data-gwp-preorder-date]");
    if (!el) return "";
    return String(el.getAttribute("data-gwp-preorder-date") || "").replace(/\s+/g, " ").trim();
  };

  const resolvePreorderDate = (item, response) => {
    return (
      configuredPreorderDate() ||
      giftLinePreorderDate(item) ||
      dateFromResponse(response) ||
      readDomPreorderDate()
    );
  };

  const buildGiftProperties = (date) => {
    const props = { [LINE_ITEM_PROP]: "true" };
    const trimmed = String(date || "").replace(/\s+/g, " ").trim();
    if (trimmed) props[PREORDER_PROP] = trimmed;
    return props;
  };

  const signatureForCart = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items
      .map((i) => `${i.key}:${i.quantity}:${isGiftItem(i) ? "g" : "n"}:${isGiftItem(i) ? giftLinePreorderDate(i) : ""}`)
      .join("|");
  };

  const getSectionInnerHTML = (html, selector) => {
    try {
      return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML || "";
    } catch (_) {
      return "";
    }
  };

  const renderSections = (sections) => {
    if (!sections) return;
    const cartDrawerHtml = sections["cart-drawer"];
    if (cartDrawerHtml) {
      const drawerRoot = document.querySelector("#CartDrawer");
      if (drawerRoot) {
        drawerRoot.innerHTML = getSectionInnerHTML(cartDrawerHtml, "#CartDrawer") || drawerRoot.innerHTML;
      }
    }
    for (const sectionId of SECTIONS_TO_RENDER) {
      if (sectionId === "cart-drawer") continue;
      const sectionHtml = sections[sectionId];
      if (!sectionHtml) continue;
      const container = document.getElementById(sectionId);
      if (!container) continue;
      const target = container.querySelector(".shopify-section") || container;
      target.innerHTML = getSectionInnerHTML(sectionHtml, ".shopify-section") || sectionHtml;
    }
  };

  const applyEmptyState = (itemCount) => {
    const isEmpty = Number(itemCount) === 0;
    const drawer = document.querySelector("cart-drawer");
    if (drawer) drawer.classList.toggle("is-empty", isEmpty);
    const drawerItems = document.querySelector("cart-drawer-items");
    if (drawerItems) drawerItems.classList.toggle("is-empty", isEmpty);
    const footer = document.getElementById("main-cart-footer");
    if (footer) footer.classList.toggle("is-empty", isEmpty);
  };

  const refreshRebuyCart = () => {
    try {
      if (window.Rebuy?.SmartCart?.fetchCart) {
        window.Rebuy.SmartCart.fetchCart();
        return;
      }
      if (window.Rebuy?.smartCart?.fetchCart) {
        window.Rebuy.smartCart.fetchCart();
        return;
      }
      document.dispatchEvent(new CustomEvent("rebuy:cart.change"));
    } catch (_) {}
  };

  const publishCartUpdate = (cart) => {
    try {
      if (typeof publish === "function" && CART_UPDATE_EVENT) {
        publish(CART_UPDATE_EVENT, cart ? { source: SOURCE, cartData: cart } : { source: SOURCE });
      }
    } catch (_) {}

    try {
      document.dispatchEvent(new CustomEvent("cart:ui:updated", { detail: { source: SOURCE, cart } }));
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: { source: SOURCE, cart } }));
    } catch (_) {}
  };

  const applyCartResponse = (response) => {
    renderSections(response?.sections);
    applyEmptyState(response?.item_count);
    refreshRebuyCart();
  };

  const changeGiftLine = async (giftLine, quantity, date) => {
    const payload = {
      id: giftLine.key,
      quantity,
      sections: SECTIONS_PARAM,
      sections_url: window.location.pathname,
    };
    if (date) {
      payload.properties = buildGiftProperties(date);
    }
    const nextCart = await postJson(changeUrl, payload);
    lastSignature = signatureForCart(nextCart);
    applyCartResponse(nextCart);
    publishCartUpdate(nextCart);
    lastHadGift = true;
    return nextCart;
  };

  const addedGiftFromResponse = (response) => {
    const items = Array.isArray(response?.items) ? response.items : [];
    return items.find((i) => Number(i.id) === variantId) || (Number(response?.id) === variantId ? response : null);
  };

  const reconcile = async () => {
    if (inFlight) {
      queued = true;
      return;
    }
    inFlight = true;

    try {
      const [cart, qualifyingCount] = await Promise.all([fetchCart(), fetchQualifyingCount()]);
      const items = cart.items || [];
      const qualifiesForGift = qualifyingCount >= threshold;
      const giftLine = items.find((i) => Number(i.id) === variantId && isGiftItem(i));
      const hasGiftNow = Boolean(giftLine);
      const knownDate = configuredPreorderDate() || readDomPreorderDate();
      const giftNeedsDate =
        Boolean(giftLine) && Boolean(knownDate) && giftLinePreorderDate(giftLine) !== knownDate;

      const sig = signatureForCart(cart);
      if (sig === lastSignature && !giftNeedsDate) return;

      if (!hasGiftNow && lastHadGift && qualifiesForGift && !systemRemovingGift && !systemAddingGift) {
        setOptedOut();
      }

      lastSignature = sig;

      if (isOptedOut()) {
        lastHadGift = hasGiftNow;
        return;
      }

      if (qualifiesForGift) {
        if (!giftLine) {
          systemAddingGift = true;
          try {
            const addDate = configuredPreorderDate();
            const addRes = await postJson(addUrl, {
              items: [
                {
                  id: variantId,
                  quantity: 1,
                  properties: buildGiftProperties(addDate),
                },
              ],
              sections: SECTIONS_PARAM,
              sections_url: window.location.pathname,
            });
            applyCartResponse(addRes);
            lastHadGift = true;

            const added = addedGiftFromResponse(addRes);
            const date =
              configuredPreorderDate() ||
              giftLinePreorderDate(added) ||
              dateFromResponse(addRes) ||
              readDomPreorderDate();
            if (date && giftLinePreorderDate(added) !== date && added?.key) {
              await changeGiftLine(added, 1, date);
            } else {
              lastSignature = null;
              publishCartUpdate(addRes);
            }
          } catch (err) {
            lastSignature = null;
            throw err;
          } finally {
            systemAddingGift = false;
          }
        } else {
          const date = resolvePreorderDate(giftLine);
          const qtyWrong = Number(giftLine.quantity) !== 1;
          const dateMissing = Boolean(date) && giftLinePreorderDate(giftLine) !== date;
          if (qtyWrong || dateMissing) {
            try {
              await changeGiftLine(giftLine, 1, date || null);
            } catch (err) {
              lastSignature = null;
              throw err;
            }
          } else {
            lastHadGift = true;
          }
        }
      } else if (giftLine) {
        systemRemovingGift = true;
        try {
          const nextCart = await postJson(changeUrl, {
            id: giftLine.key,
            quantity: 0,
            sections: SECTIONS_PARAM,
            sections_url: window.location.pathname,
          });
          lastSignature = signatureForCart(nextCart);
          applyCartResponse(nextCart);
          publishCartUpdate(nextCart);
          lastHadGift = false;
        } catch (err) {
          lastSignature = null;
          throw err;
        } finally {
          systemRemovingGift = false;
        }
      } else {
        lastHadGift = false;
      }
    } catch (err) {
      console.warn("[GWP]", err);
    } finally {
      inFlight = false;
      if (queued) {
        queued = false;
        setTimeout(reconcile, 0);
      }
    }
  };

  const schedule = (() => {
    let t;
    return () => {
      clearTimeout(t);
      t = setTimeout(reconcile, 50);
    };
  })();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => schedule(), { once: true });
  } else {
    schedule();
  }

  try {
    if (typeof subscribe === "function" && CART_UPDATE_EVENT) {
      subscribe(CART_UPDATE_EVENT, (event) => {
        if (event?.source === SOURCE) return;
        schedule();
      });
    }
  } catch (_) {}

  document.addEventListener("cart:ui:updated", (event) => {
    if (event?.detail?.source === SOURCE) return;
    schedule();
  });
  document.addEventListener("cart:addtocart_complete", () => schedule());
})();
